import React from "react";

// Lightweight, animated SVG icons that actually represent each cosmetic type
// (wheels, trails, boost jets, smash bursts, paint chips, title badges) tinted
// by the cosmetic colour — far richer than a flat coloured square.

function lighten(hex, amt = 40) {
  if (!hex || hex[0] !== "#" || hex.length !== 7) return hex || "#fff";
  const n = (i) => Math.min(255, parseInt(hex.slice(i, i + 2), 16) + amt);
  return `rgb(${n(1)},${n(3)},${n(5)})`;
}
function darken(hex, amt = 40) {
  if (!hex || hex[0] !== "#" || hex.length !== 7) return hex || "#000";
  const n = (i) => Math.max(0, parseInt(hex.slice(i, i + 2), 16) - amt);
  return `rgb(${n(1)},${n(3)},${n(5)})`;
}

export function CosmeticIcon({ type, color = "#FF9500", size = 44 }) {
  const c = color;
  const lite = lighten(c, 70);
  const dark = darken(c, 60);
  const common = { width: size, height: size, viewBox: "0 0 48 48" };

  if (type === "wheel") {
    return (
      <svg {...common} className="ci-wheel" data-testid="ci-wheel">
        <circle cx="24" cy="24" r="21" fill="#0d0d12" />
        <circle cx="24" cy="24" r="21" fill="none" stroke={dark} strokeWidth="1.5" />
        <circle cx="24" cy="24" r="14" fill={c} />
        <circle cx="24" cy="24" r="14" fill="none" stroke={lite} strokeWidth="1.2" />
        <g className="ci-spin" style={{ transformOrigin: "24px 24px" }}>
          {[0, 60, 120, 180, 240, 300].map((a) => (
            <rect key={a} x="23" y="12" width="2" height="12" rx="1" fill={dark}
              transform={`rotate(${a} 24 24)`} />
          ))}
        </g>
        <circle cx="24" cy="24" r="4.5" fill={lite} />
        <circle cx="21" cy="21" r="1.6" fill="rgba(255,255,255,0.7)" />
      </svg>
    );
  }

  if (type === "trail") {
    return (
      <svg {...common} data-testid="ci-trail">
        <defs>
          <linearGradient id={`tg-${c}`} x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor={c} stopOpacity="0" />
            <stop offset="100%" stopColor={lite} stopOpacity="1" />
          </linearGradient>
        </defs>
        <g className="ci-trail-streaks">
          <rect x="2" y="14" width="40" height="4" rx="2" fill={`url(#tg-${c})`} />
          <rect x="8" y="22" width="36" height="5" rx="2.5" fill={`url(#tg-${c})`} />
          <rect x="4" y="31" width="38" height="4" rx="2" fill={`url(#tg-${c})`} />
        </g>
        <circle cx="42" cy="24" r="5" fill={c} style={{ filter: `drop-shadow(0 0 5px ${c})` }} />
      </svg>
    );
  }

  if (type === "boost") {
    return (
      <svg {...common} className="ci-flicker" data-testid="ci-boost">
        <path d="M40 24 L16 12 Q4 18 6 24 Q4 30 16 36 Z" fill={c}
          style={{ filter: `drop-shadow(0 0 6px ${c})` }} />
        <path d="M34 24 L18 16 Q11 20 12 24 Q11 28 18 32 Z" fill={lite} />
        <path d="M27 24 L20 20 Q17 22 17 24 Q17 26 20 28 Z" fill="#fff" />
      </svg>
    );
  }

  if (type === "smash") {
    return (
      <svg {...common} className="ci-pulse" data-testid="ci-smash">
        <g style={{ transformOrigin: "24px 24px" }}>
          {[0, 45, 90, 135, 180, 225, 270, 315].map((a) => (
            <path key={a} d="M24 24 L22 6 L24 3 L26 6 Z" fill={a % 90 === 0 ? c : lite}
              transform={`rotate(${a} 24 24)`} />
          ))}
        </g>
        <circle cx="24" cy="24" r="6.5" fill={c} style={{ filter: `drop-shadow(0 0 6px ${c})` }} />
        <circle cx="24" cy="24" r="2.5" fill="#fff" />
      </svg>
    );
  }

  if (type === "paint") {
    return (
      <svg {...common} data-testid="ci-paint">
        <path d="M24 4 C33 14 40 22 40 30 A16 16 0 1 1 8 30 C8 22 15 14 24 4 Z" fill={c} />
        <path d="M24 4 C33 14 40 22 40 30 A16 16 0 1 1 8 30 C8 22 15 14 24 4 Z" fill="none" stroke={dark} strokeWidth="1.5" />
        <ellipse cx="18" cy="26" rx="4" ry="7" fill="rgba(255,255,255,0.45)" />
      </svg>
    );
  }

  if (type === "title") {
    return (
      <svg {...common} data-testid="ci-title">
        <path d="M8 8 H40 V30 L24 40 L8 30 Z" fill={c} />
        <path d="M8 8 H40 V30 L24 40 L8 30 Z" fill="none" stroke={lite} strokeWidth="1.5" />
        <path d="M24 13 l2.4 4.9 5.4 .8 -3.9 3.8 .9 5.4 -4.8 -2.5 -4.8 2.5 .9 -5.4 -3.9 -3.8 5.4 -.8 Z"
          fill="#fff" opacity="0.92" />
      </svg>
    );
  }

  if (type === "skin") {
    return (
      <svg {...common} data-testid="ci-skin">
        <ellipse cx="24" cy="40" rx="14" ry="3" fill="rgba(0,0,0,0.4)" />
        <rect x="8" y="10" width="10" height="6" rx="2" fill="#111" />
        <rect x="30" y="10" width="10" height="6" rx="2" fill="#111" />
        <rect x="8" y="30" width="10" height="6" rx="2" fill="#111" />
        <rect x="30" y="30" width="10" height="6" rx="2" fill="#111" />
        <rect x="10" y="14" width="28" height="18" rx="6" fill={c} stroke={dark} strokeWidth="1.5" />
        <rect x="18" y="17" width="14" height="12" rx="3" fill="rgba(120,205,255,0.5)" />
        <rect x="11" y="15" width="26" height="4" rx="2" fill="rgba(255,255,255,0.25)" />
      </svg>
    );
  }

  // fallback glossy chip
  return (
    <svg {...common} data-testid="ci-chip">
      <rect x="6" y="6" width="36" height="36" rx="10" fill={c} stroke={lite} strokeWidth="1.5" />
      <rect x="10" y="10" width="28" height="10" rx="5" fill="rgba(255,255,255,0.25)" />
    </svg>
  );
}
